import { observer } from 'mobx-react-lite';

import { EditBoolean } from 'components/forms/EditBoolean';
import { EditDate } from 'components/forms/EditDate';
import { EditTime } from 'components/forms/EditTime';

interface Props {
  readonly label: string;
  readonly date: string | null;
  readonly onDateChange: (value: string | null) => void;
  readonly hasTime: boolean;
  readonly onHasTimeChange: (value: boolean) => void;
  readonly time: string | null;
  readonly onTimeChange: (value: string | null) => void;
  /** Label for the checkbox, e.g. "Set start time on the first day". */
  readonly timeLabel: string;
  readonly timeInfo: string;
  /** Seeded into the time when the override is first switched on. */
  readonly defaultTime: string;
}

/**
 * One end of a rule's date range: the day, and an optional time-of-day that
 * overrides the rule's own start or end time on that one day.
 *
 * The checkbox and the time input only appear once a day is picked — a time
 * override with no day to apply it to means nothing. Switching the override on
 * seeds a sensible hour so the operator is not left with an empty required-looking
 * field; switching it off leaves the time alone, so the model can hand it back
 * unchanged if they switch it on again.
 *
 * Deliberately not in `components/forms`: the "override the time on the first day
 * only" idea belongs to scheduling windows, not to forms in general.
 */
export const EditDateBound = observer<Props>(
  ({ label, date, onDateChange, hasTime, onHasTimeChange, time, onTimeChange, timeLabel, timeInfo, defaultTime }) => (
    <>
      <EditDate label={label} value={date} onChange={onDateChange} />
      {date !== null && (
        <div className="mt-2">
          <EditBoolean
            label={timeLabel}
            value={hasTime}
            onChange={(checked) => {
              onHasTimeChange(checked);
              if (checked && time === null) onTimeChange(defaultTime);
            }}
            info={timeInfo}
          />
          {hasTime && (
            <div className="mt-2">
              <EditTime label={timeLabel} value={time} onChange={onTimeChange} seconds />
            </div>
          )}
        </div>
      )}
    </>
  )
);
